import { useState } from 'react';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';

export interface CapturedPhoto {
  dataUrl: string;
  timestamp: string;
  latitude?: number;
  longitude?: number;
  path?: string;
}

export function useNativeCamera() {
  const [isCapturing, setIsCapturing] = useState(false);

  const capturePhoto = async (): Promise<CapturedPhoto | null> => {
    setIsCapturing(true);

    try {
      // Request location permission and get current position
      let latitude: number | undefined;
      let longitude: number | undefined;

      try {
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 5000
        });
        latitude = position.coords.latitude;
        longitude = position.coords.longitude;
      } catch (geoError) {
        console.warn('Geolocation not available:', geoError);
        // Continue without geolocation
      }

      // Capture photo using native camera
      const photo: Photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Prompt,
        saveToGallery: false,
      });

      if (!photo.dataUrl) {
        throw new Error('No image data captured');
      }

      const timestamp = new Date().toISOString();

      return {
        dataUrl: photo.dataUrl,
        timestamp,
        latitude,
        longitude,
        path: photo.path
      };
    } catch (error: any) {
      console.error('Camera capture error:', error);
      
      if (error.message === 'User cancelled photos app') {
        toast.info('Photo capture cancelled');
      } else {
        toast.error('Failed to capture photo: ' + error.message);
      }
      
      return null;
    } finally {
      setIsCapturing(false);
    }
  };

  const isNativeAvailable = Capacitor.isNativePlatform();

  return {
    capturePhoto,
    isCapturing,
    isNativeAvailable
  };
}
